import { create } from '@bufbuild/protobuf'
import {
  DateSchema,
  type Date as ProtoDate,
} from '@ebalance/proto/google/type/date_pb'

import { now as serverNow } from './clock'
import { jstYmd } from './dateTime'

/**
 * google.type.Date ({ year, month, day }) と `<input type="date">` の value (YYYY-MM-DD)
 * の相互変換 helper。
 *
 * OCCTO マスタの日付フィールド (BG 契約開始日 / 計画提出者の適用開始・終了日) で共通利用する
 * (= 旧来は各 FormFields が同じ split / padStart ロジックを inline で重複していた)。
 * 時刻を持たない暦日のみを扱うため Date は経由しない。
 */
export type CivilDate = { year: number; month: number; day: number }

/**
 * 「今日」の civil.Date を返す。
 * 適用開始日・有効開始日のデフォルト (spec bg.md L25「デフォルト: 今日」) として、
 * 年度始まりではなく実行時点の暦日をデフォルトにしたいケースで使う。
 * `new Date()` は呼び出し時点を評価するため getter 関数として提供する。
 */
export function todayCivilDate(): CivilDate {
  return jstYmd(serverNow())
}

/**
 * CivilDate 同士の比較 (負: a < b、0: a == b、正: a > b)。 呼び出し側は符号
 * (`< 0` / `<= 0` 等) のみを見ればよく、 差分の絶対値には意味を持たせない
 * (year が異なれば month/day 分の桁は無視されるため厳密な日数差ではない)。
 * OCCTO マスタ各タブの適用期間比較 (内包制約 / 期限判定) で共有する。
 */
export function compareCivilDate(a: CivilDate, b: CivilDate): number {
  if (a.year !== b.year) return a.year - b.year
  if (a.month !== b.month) return a.month - b.month
  return a.day - b.day
}

/**
 * CivilDate (google.type.Date 互換) を "YYYY/MM/DD" に整形する (timezone 変換を挟まない)。
 * 未設定 (`undefined`) は空文字。 画面の日付表示と CSV の日付セルで共有する。
 */
export function formatCivilDateSlash(d: CivilDate | undefined): string {
  if (d === undefined) return ''
  return `${d.year}/${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}`
}

/** Returns the later of two calendar dates. */
export function maxCivilDate(a: CivilDate, b: CivilDate): CivilDate {
  return compareCivilDate(a, b) >= 0 ? a : b
}

/**
 * CivilDate 同士が等しいか判定する (`undefined` / `null` はどちらも
 * 「未設定」として扱い、 両方未設定の時のみ等しいとみなす — RHF field-array
 * 内のネスト path は `clearAsNull` 契約により `null` を経由するため
 * `null` も受け付ける、 `~/pages/AdminOcctoMaster/tabs/_common/components/
 * PeriodFields` 参照)。 BG マスタの代表事業者行の適用期間が BG 本体と
 * 一致しているかの同期判定 (AC-OMB-021) 等、 差分の有無だけを見たい
 * 呼び出し側で `compareCivilDate` の代わりに使う。
 */
export function civilDateEquals(
  a: CivilDate | null | undefined,
  b: CivilDate | null | undefined,
): boolean {
  const normalizedA = a ?? undefined
  const normalizedB = b ?? undefined
  if (normalizedA === undefined || normalizedB === undefined) {
    return normalizedA === normalizedB
  }
  return compareCivilDate(normalizedA, normalizedB) === 0
}

/**
 * 暦上実在する日付か判定する (2026/9/31 や 2027/2/29 のような月ごとの日数・
 * 閏年を無視した組み合わせを弾く)。
 *
 * backend の converter は `civil.Date.IsValid()` で同じ判定を行い
 * InvalidArgument を返すが、 CSV 取込では行番号を伴わない技術的エラー文で
 * 表面化してユーザーが原因行を特定できないため、 取込前の parse 層でも同じ
 * 判定を行う (二重化)。
 */
export function isValidCivilDate({ year, month, day }: CivilDate): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return false
  }
  if (month < 1 || month > 12 || day < 1) return false
  // UTC で構築して local timezone の DST / offset による日付ずれを避ける。
  const d = new Date(Date.UTC(year, month - 1, day))
  d.setUTCFullYear(year) // 0-99 年が 1900 年台に丸められるのを戻す
  return (
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
  )
}

/**
 * google.type.Date を `<input type="date">` の value (YYYY-MM-DD) に変換する。
 * 未設定 (year/month/day のいずれかが欠落) のときは空文字を返す。
 */
export function civilDateToInputValue(date: CivilDate | undefined): string {
  if (
    date?.year === undefined ||
    date.month === undefined ||
    date.day === undefined
  ) {
    return ''
  }
  return `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

/**
 * `<input type="date">` の value (YYYY-MM-DD) を google.type.Date に変換する。
 * 空文字や YYYY-MM-DD 形式でない値は undefined を返す。
 */
export function parseDateInputValue(value: string): CivilDate | undefined {
  if (value === '') {
    return undefined
  }
  const parts = value.split('-')
  if (parts.length !== 3) {
    return undefined
  }
  return {
    year: Number(parts[0]),
    month: Number(parts[1]),
    day: Number(parts[2]),
  }
}

/**
 * CivilDate → JS `Date` (= ローカルタイムゾーンの 0:00)。
 *
 * `DateStepper` / `DateNav` 等、 状態を `Date` で持つ既存呼び出し側を
 * `CompactCivilDateField` (CivilDate ベース) へ繋ぐための橋渡し
 * (2026-08-12、 Dev 画面の raw `<Input type="date">` を CompactCivilDateField へ
 * 移行する対応で追加)。 proto Date 経由の `~/models/dateTime.ts` の
 * `fromProtoDate` と同じ変換だが、 CivilDate はプレーンオブジェクトで proto
 * message ではないため独立して持つ。
 */
export function civilDateToDate(date: CivilDate): Date {
  return new Date(date.year, date.month - 1, date.day)
}

/**
 * JS `Date` → CivilDate (= `civilDateToDate` の逆)。
 */
export function dateToCivilDate(date: Date): CivilDate {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  }
}

/**
 * CivilDate を google.type.Date (proto message) に変換する。
 */
export function civilDateToProtoDate(date: CivilDate): ProtoDate {
  return create(DateSchema, {
    year: date.year,
    month: date.month,
    day: date.day,
  })
}

/**
 * google.type.Date (proto message) を CivilDate に変換する。
 * nil / 未設定のときは undefined を返す。
 */
export function protoDateToCivilDate(
  date: ProtoDate | undefined,
): CivilDate | undefined {
  if (date === undefined) {
    return undefined
  }
  if (date.year === 0 || date.month === 0 || date.day === 0) {
    return undefined
  }
  return {
    year: date.year,
    month: date.month,
    day: date.day,
  }
}

/**
 * 契約上必須の日付 field (例: 広域マスタ一覧の `validFrom`) の欠落を契約違反として throw する。
 *
 * 欠落を epoch 等の値で埋めると、データ異常が「有効な業務データ」として一覧・失効判定に
 * 紛れ込むため fallback しない (#3818)。 `context` は欠落元を特定できる文字列
 * (message 名・field 名・id) を渡す。
 */
export function requireCivilDate(
  date: CivilDate | undefined,
  context: string,
): CivilDate {
  if (date === undefined) {
    throw new Error(`required date is missing: ${context}`)
  }
  return date
}
